import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import Loading from '../../components/loading';
import FinancialCategoryFormModal from './financialCategoryFormModal';
import { useFinancialCategories } from './hooks/useFinancialCategories';
import { useDeleteFinancialCategory } from './hooks/useDeleteFinancialCategory';

export default function FinancialCategoryList({ title, entityLabel, basePath }) {
  const navigate = useNavigate();

  const {
    categories,
    isLoading,
    error: listError,
    refetch: getCategories,
  } = useFinancialCategories(basePath);
  const { remove } = useDeleteFinancialCategory(basePath);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, categoryId) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategoryId(categoryId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedCategory(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const category = categories.find((c) => c.id === selectedCategoryId);
    if (category) {
      setSelectedCategory(category);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedCategoryId(null);
  };

  const handleCategorySaved = () => {
    getCategories();
    setSuccessMessage('Categoria salva com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedCategoryId)
      .then((res) => {
        getCategories();
        setSuccessMessage(res.data ?? 'Categoria excluída com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir categoria.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedCategoryId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: '600' }}>
            {title}
          </Typography>
          <Button variant="outlined" onClick={openCreateModal} sx={{ flexShrink: 0 }}>
            Adicionar
          </Button>
        </Box>

        <List>
          {categories.length === 0 && !isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Nenhuma categoria cadastrada
            </Typography>
          ) : (
            categories.map((category) => (
              <React.Fragment key={category.id}>
                <ListItem
                  secondaryAction={
                    <IconButton onClick={(event) => clickMenu(event, category.id)}>
                      <MoreVertIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={category.description} />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
        <MenuItem onClick={openEditModal}>Editar</MenuItem>
        <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <FinancialCategoryFormModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={handleCategorySaved}
        basePath={basePath}
        category={selectedCategory}
        entityLabel={entityLabel}
      />

      <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você tem certeza que deseja excluir esta categoria? Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmModal}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

FinancialCategoryList.propTypes = {
  title: PropTypes.string.isRequired,
  entityLabel: PropTypes.string.isRequired,
  basePath: PropTypes.string.isRequired,
};
